import { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Printer, ArrowLeft, CheckCircle2 } from 'lucide-react'
import { getInhouseRequest, acceptInhouseRequest, type InhouseRequest } from '@/services/inhouseService'
import { JOSCOWORK_COMPANY, INHOUSE_DAILY_RATE } from '@/lib/joscoworkCompany'
import { thaiBahtText } from '@/lib/thaiBahtText'
import { useAuthStore } from '@/store/authStore'
import { type InvoiceInfo, emptyInvoiceInfo } from '@/types/invoiceInfo'
import InvoiceInfoForm from '@/components/InvoiceInfoForm'
import applyBg from '@/pages/JobApplication/assets/apply-bg.png'

const STATUS_LABEL: Record<string, string> = {
  NEW: 'รอทีมงานติดต่อกลับ',
  CONTACTED: 'ทีมงานติดต่อกลับแล้ว',
  QUOTED: 'ส่งใบเสนอราคาแล้ว',
  CLOSED: 'ปิดคำขอ',
}

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })
const formatDateShort = (value: string) =>
  new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })

const money = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// จำนวนวันอบรม (รวมวันแรกและวันสุดท้าย) — สำรองไว้เผื่อคำขอเก่าที่ backend ยังไม่มี unit_price/total_price (ก่อนอัปเดตฟีเจอร์นี้)
function countDays(dateFrom: string, dateTo: string): number {
  const from = new Date(dateFrom)
  const to = new Date(dateTo)
  const diff = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
  return Math.max(1, diff + 1)
}

// CSS พิมพ์หลักของไซต์ตั้งไว้สำหรับใบประกาศ (A4 แนวนอน + ล็อกความสูงหน้า) — เอกสารนี้เป็น A4 แนวตั้ง
// จึงแทรกสไตล์เฉพาะหน้านี้ทับตอนเปิดหน้า แล้วเอาออกตอนออกจากหน้า (ไม่กระทบการพิมพ์ใบประกาศ)
const PRINT_CSS = `
@media print {
  @page { size: 210mm 297mm; margin: 14mm; }
  html, body { height: auto !important; overflow: visible !important; }
  body * { visibility: hidden; }
  .quote-print-area, .quote-print-area * { visibility: visible; }
  .quote-print-area * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .quote-print-area {
    position: absolute !important;
    left: 0; top: 0;
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    /* @page เว้นขอบ 14mm บน-ล่างไปแล้ว (รวม 28mm) — ต้องลบออกจาก min-height ที่ตั้งไว้ตอน preview (297mm)
       ไม่งั้นความสูงรวมเกินกระดาษจริง ทำให้ล้นไปหน้า 2 */
    min-height: calc(297mm - 28mm) !important;
    height: auto !important;
  }
}
`

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span>{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

// เอกสาร "ใบเสนอราคา" — ราคาคำนวณอัตโนมัติทันที (เหมา 35,000 บาท/วัน) ไม่ต้องรอแอดมินใส่ราคา
// แสดงด้วย HTML/CSS แล้วพิมพ์เป็น PDF ด้วย window.print() (วิธีเดียวกับใบประกาศ/ใบเสร็จ ไม่ต้องมีไลบรารีสร้าง PDF)
export default function InhouseRequestPage() {
  const { id } = useParams<{ id: string }>()
  const customerType = useAuthStore((s) => s.customerType)
  const isCompany = customerType === 'COMPANY'
  const [request, setRequest] = useState<InhouseRequest | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [wantsInvoice, setWantsInvoice] = useState(false)
  // คำขอ In-house เป็นของบริษัทเสมอในทางธุรกิจ — ใบแจ้งหนี้จึงล็อกเป็นนิติบุคคล/บริษัทเสมอ ไม่มีตัวเลือกบุคคลทั่วไป
  const [invoiceInfo, setInvoiceInfo] = useState<InvoiceInfo>(() => emptyInvoiceInfo('COMPANY', false))
  const [accepting, setAccepting] = useState(false)
  const [acceptError, setAcceptError] = useState('')

  useEffect(() => {
    const style = document.createElement('style')
    style.id = 'quote-print-style'
    style.textContent = PRINT_CSS
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  useEffect(() => {
    if (!id) return
    getInhouseRequest(id)
      .then(setRequest)
      .catch((err) => setError(err instanceof Error ? err.message : 'ไม่พบคำขอ'))
      .finally(() => setLoading(false))
  }, [id])

  const days = request ? countDays(request.date_from, request.date_to) : 0
  const unitPrice = request?.unit_price ?? INHOUSE_DAILY_RATE
  const subtotal = request?.total_price ?? days * unitPrice
  const vat = Math.round(subtotal * 0.07 * 100) / 100
  const withholding = Math.round(subtotal * 0.03 * 100) / 100
  const grandTotal = subtotal + vat - withholding

  const dateRangeText = useMemo(() => {
    if (!request) return ''
    return request.date_from === request.date_to
      ? formatDateShort(request.date_from)
      : `${formatDateShort(request.date_from)} - ${formatDateShort(request.date_to)}`
  }, [request])

  const handleAccept = async () => {
    if (!request) return
    setAccepting(true)
    setAcceptError('')
    try {
      const updated = await acceptInhouseRequest(request.request_id, wantsInvoice ? invoiceInfo : undefined)
      setRequest(updated)
    } catch (err) {
      setAcceptError(err instanceof Error ? err.message : 'ยอมรับใบเสนอราคาไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
    } finally {
      setAccepting(false)
    }
  }

  if (request?.accepted_at) {
    return (
      <div className="relative isolate flex min-h-screen items-center justify-center overflow-hidden bg-[#FAFAFB] pb-16 pt-24">
        {/* พื้นหลัง logistics แบบจางๆ — ใช้ภาพเดียวกับหน้าสมัครงาน (JobApplication) ให้ธีมเว็บสม่ำเสมอกัน */}
        <img src={applyBg} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
        <div className="relative mx-auto max-w-xl px-4 text-center">
          <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-accent" />
          <h1 className="mb-2 text-xl font-bold text-text">เราติดต่อผู้สอนเรียบร้อยแล้ว</h1>
          {/* textWrap: balance — ข้อความภาษาไทยไม่มีช่องว่าง ไม่ใช้จะตัดบรรทัดทิ้งคำเดียวโดดๆ ท้ายย่อหน้า */}
          <p className="mb-1 text-sm text-text-muted" style={{ textWrap: 'balance' }}>คำขอ {request.request_number} — {request.course?.title ?? ''}</p>
          <p className="mb-8 text-sm text-text-muted" style={{ textWrap: 'balance' }}>ทีมงานจะติดต่อกลับเพื่อนัดหมายและแจ้งขั้นตอนการชำระเงิน</p>
          {request.billing_info && (
            <Link
              to={`/inhouse-requests/${request.request_id}/invoice`}
              className="mb-3 inline-block text-sm font-medium text-accent hover:underline"
            >
              ดูใบแจ้งหนี้
            </Link>
          )}
          <Link
            to="/account?tab=inhouse"
            className="block border border-accent bg-accent px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent2 hover:border-accent2"
          >
            กลับไปรายการคำขอ In-house
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#FAFAFB] pb-16 pt-24">
      <div className="mx-auto max-w-4xl px-4 md:px-6">
        <div className="mb-6 flex items-center justify-between print:hidden">
          <Link to="/account?tab=inhouse" className="flex items-center gap-1.5 text-sm text-text-muted hover:text-accent">
            <ArrowLeft className="h-4 w-4" />
            กลับไปรายการคำขอ In-house
          </Link>
          {request && (
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-accent to-accent2 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:shadow-accent/40"
            >
              <Printer className="h-4 w-4" />
              ดาวน์โหลด PDF
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-sm text-text-muted">กำลังโหลด...</p>
        ) : error ? (
          <p className="text-sm text-rose-500">{error}</p>
        ) : request ? (
          <>
            <div
              className="quote-print-area mx-auto flex flex-col border border-black bg-white p-8 text-[#000] md:p-12"
              style={{ fontFamily: "'Sarabun', sans-serif", width: '210mm', minHeight: '297mm' }}
            >
              {/* หัวเอกสาร */}
              <div className="mb-6 text-center">
                <h1 className="text-2xl font-bold">ใบเสนอราคา</h1>
                <p className="mt-1 text-xs">วันที่ {formatDate(request.created_date)}</p>
              </div>

              <div className="mb-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <p className="text-sm">เรื่อง &nbsp; เสนอราคาค่าฝึกอบรม</p>
                  <p className="mt-1 text-sm">เรียน &nbsp; {request.company_name || request.contact_name}</p>
                </div>
                <div className="border border-black px-4 py-3 text-xs">
                  <p className="font-bold">{JOSCOWORK_COMPANY.name}</p>
                  <p>{JOSCOWORK_COMPANY.addressLine1}</p>
                  <p>{JOSCOWORK_COMPANY.addressLine2}</p>
                  <p>Tel. {JOSCOWORK_COMPANY.tel} · เลขผู้เสียภาษี {JOSCOWORK_COMPANY.taxId}</p>
                </div>
              </div>

              <p className="mb-4 text-sm">มีความยินดีที่จะเสนอราคาค่าบริการฝึกอบรมเพื่อให้พิจารณา ดังต่อไปนี้</p>

              {/* ตารางรายการ */}
              <table className="mb-4 w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="w-10 py-2 pl-1 text-xs font-semibold">ลำดับ</th>
                    <th className="py-2 text-xs font-semibold">รายการ</th>
                    <th className="w-20 py-2 text-right text-xs font-semibold">จำนวน</th>
                    <th className="w-24 py-2 text-right text-xs font-semibold">ราคา/วัน</th>
                    <th className="w-28 py-2 pr-1 text-right text-xs font-semibold">จำนวนเงิน</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-black align-top">
                    <td className="py-2.5 pl-1">1</td>
                    <td className="py-2.5">
                      หลักสูตร {request.course?.title ?? '-'}
                      <br />
                      <span className="text-xs">อบรมวันที่ {dateRangeText} · ผู้เข้าอบรม {request.trainee_count} คน · {request.location}</span>
                    </td>
                    <td className="py-2.5 text-right">{days} วัน</td>
                    <td className="py-2.5 text-right">{money(unitPrice)}</td>
                    <td className="py-2.5 pr-1 text-right">{money(subtotal)}</td>
                  </tr>
                </tbody>
              </table>

              {/* สรุปยอด */}
              <div className="mb-4 flex justify-end">
                <div className="w-64 space-y-1.5 border border-black px-4 py-3">
                  <Row label="รวมเป็นเงิน" value={money(subtotal)} />
                  <Row label="ภาษีมูลค่าเพิ่ม 7%" value={money(vat)} />
                  <Row label="หัก ณ ที่จ่าย 3%" value={`-${money(withholding)}`} />
                  <div className="border-t-2 border-black pt-1.5">
                    <div className="flex justify-between font-bold">
                      <span>รวมเงินทั้งสิ้น</span>
                      <span>฿{money(grandTotal)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="mb-6 text-sm">
                จำนวนเงิน (ตัวอักษร) &nbsp;
                <span className="font-medium">{thaiBahtText(grandTotal)}</span>
              </p>

              {/* ข้อกำหนด/ลายเซ็น/ท้ายเอกสาร — ดันลงล่างสุดของหน้ากระดาษเสมอ ไม่ว่าเนื้อหาด้านบนจะสั้นแค่ไหน */}
              <div className="mt-auto">
                <div className="mb-8 grid grid-cols-1 gap-6 border-t border-black pt-5 text-xs sm:grid-cols-2">
                  <div>
                    <p className="mb-1 font-semibold">ข้อกำหนดในการเสนอราคา</p>
                    <ul className="list-disc space-y-1 pl-4">
                      <li>ราคาที่เสนอเป็นหน่วยเงินบาทไทย</li>
                      <li>ราคานี้มีผลภายใน 30 วันนับจากวันที่เสนอราคา</li>
                      <li>สามารถชำระค่าลงทะเบียนโอนเงินเข้าบัญชี ชื่อบัญชี {JOSCOWORK_COMPANY.name}</li>
                      <li>{JOSCOWORK_COMPANY.bankName} เลขที่ {JOSCOWORK_COMPANY.bankAccountNumber}</li>
                    </ul>
                  </div>
                  <div className="text-right">
                    {/* ผู้เสนอราคา = ผู้สอนของคอร์สนี้ (m_instructor: ชื่อ + signature_url) — ลายเซ็นวางทับเส้นประ */}
                    <div className="relative mb-8 inline-block">
                      <p className="relative z-0">ลงชื่อ ………………………………………</p>
                      {request.course?.instructor?.signature_url && (
                        <img
                          src={request.course.instructor.signature_url} alt="ลายเซ็นผู้เสนอราคา"
                          className="pointer-events-none absolute bottom-0.5 right-2 z-10 h-11 w-auto select-none"
                        />
                      )}
                    </div>
                    {request.course?.instructor && (
                      <p className="font-semibold">({request.course.instructor.first_name} {request.course.instructor.last_name})</p>
                    )}
                    <p>ผู้เสนอราคา</p>
                  </div>
                </div>

                <p className="border-t border-black pt-4 text-center text-[11px]">
                  เอกสารนี้ออกโดยระบบ JosCoWork · {request.request_number} · สถานะ: {STATUS_LABEL[request.status] ?? request.status}
                </p>
              </div>
            </div>

            {/* ตัวเลือกขอใบแจ้งหนี้ — ไม่บังคับไม่ว่าบัญชีประเภทไหน (ต่างจากใบเสร็จซื้อคอร์สที่บังคับสำหรับบัญชีบริษัท) */}
            <div className="mt-6 print:hidden">
              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-text">
                <input
                  type="checkbox" className="h-4 w-4 accent-indigo-500"
                  checked={wantsInvoice}
                  onChange={(e) => setWantsInvoice(e.target.checked)}
                />
                ต้องการใบแจ้งหนี้ด้วย
              </label>
            </div>

            {wantsInvoice && (
              <div className="mt-4 print:hidden">
                <InvoiceInfoForm
                  isCompany={isCompany}
                  info={invoiceInfo}
                  onChange={setInvoiceInfo}
                  alwaysOptional
                  companyOnly
                  title="ข้อมูลสำหรับออกใบแจ้งหนี้"
                  subtitle="กรอกข้อมูลบริษัทที่จะให้ระบุในใบแจ้งหนี้"
                />
              </div>
            )}

            <div className="mt-6 flex items-center justify-between border border-[#E2E4EA] bg-white px-6 py-5 print:hidden">
              <div>
                <p className="text-sm font-medium text-text">ยืนยันยอมรับใบเสนอราคานี้</p>
                <p className="mt-0.5 text-xs text-text-muted">เมื่อกดยอมรับแล้ว ทีมงานจะได้รับแจ้งและติดต่อกลับเพื่อดำเนินการต่อ</p>
                {acceptError && <p className="mt-1.5 text-xs text-rose-500">{acceptError}</p>}
              </div>
              <button
                onClick={handleAccept}
                disabled={accepting}
                className="shrink-0 border border-accent bg-accent px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent2 hover:border-accent2 disabled:opacity-60"
              >
                {accepting ? 'กำลังส่ง...' : 'ยอมรับใบเสนอราคา'}
              </button>
            </div>
            <p className="mt-3 text-center text-xs text-text-muted print:hidden">
              กด &quot;ดาวน์โหลด PDF&quot; แล้วเลือก &quot;บันทึกเป็น PDF&quot; ที่หน้าต่างพิมพ์ของเบราว์เซอร์
            </p>
          </>
        ) : null}
      </div>
    </div>
  )
}
